import { useEffect, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, Trash, FileXls, PaperPlaneTilt, Eye, DownloadSimple, WarningCircle,
  CheckCircle, DotsThreeVertical, Receipt, Paperclip, CopySimple, UploadSimple, PencilSimple,
  ArrowsOut, ArrowsIn, ChatCircleDots, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { printPO, previewPO } from "@/lib/poPrint";
import { hasSpecialPermission } from "@/lib/permissions";
import MaterialReceiptDialog from "@/components/MaterialReceiptDialog";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const inputCls = "bg-white border-slate-300 rounded-sm text-slate-900 text-sm h-8 focus-visible:ring-emerald-500";
const cellIn = "w-full h-full px-2 py-1.5 outline-none border-none bg-transparent text-xs focus:bg-emerald-50 transition-colors disabled:bg-transparent disabled:text-slate-500";
const xlTh = "border border-slate-300 bg-slate-100 px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 text-left";
const xlTd = "border border-slate-300 p-0";
const lbl = "text-xs font-medium text-slate-600 mb-1 block";
const blankItem = () => ({
  id: `it_${Date.now()}_${Math.floor(Math.random() * 999)}`,
  item: "", group: "", make: "", specification: "", qty: "", cat_no: "", rate: "", dis: "",
});
const calcNet = (it) => (Number(it.rate) || 0) * (1 - (Number(it.dis) || 0) / 100);
const calcTotal = (it) => calcNet(it) * (Number(it.qty) || 0);
const pickField = (v, cands) => {
  for (const f of v.fields || []) {
    const k = `${f.key} ${f.label}`.toLowerCase();
    if (cands.some((c) => k.includes(c))) {
      const val = v.data?.[f.key];
      if (String(val || "").trim()) return val;
    }
  }
  return "";
};
const vendorTitle = (v) => v.data?.[v.fields?.[0]?.key] || pickField(v, ["vendor_name", "name", "company"]) || "Vendor";
// Mirrors Vendors.js's data-quality check — surfaced here too so a bad phone/address in the
// Vendor Registry doesn't silently flow into a PO unnoticed at the point it actually matters.
const vendorQualityIssues = (v) => {
  const issues = [];
  const mobile = pickField(v, ["mobile", "phone", "whatsapp"]);
  const email = pickField(v, ["email"]);
  const address = pickField(v, ["address"]);
  const contact = pickField(v, ["contact", "person"]);
  const digits = String(mobile || "").replace(/\D/g, "");
  if (!mobile) issues.push("Missing phone number");
  else if (digits.length < 10 || digits.length > 13) issues.push(`Phone looks invalid ("${mobile}")`);
  if (!address) issues.push("Missing address");
  else if (address.trim().length < 8 || !/\s/.test(address.trim())) issues.push(`Address looks incomplete ("${address}")`);
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) issues.push(`Email looks invalid ("${email}")`);
  if (!contact) issues.push("Missing contact person");
  return issues;
};
const blankPO = (o) => ({
  vendor_id: null, vendor_name: "", vendor_address: "", concerned_person: "", contact_phone: "", contact_email: "",
  customer_name: o.customer || "", ref: o.customer_po || "", po_date: new Date().toISOString().slice(0, 10),
  dvepl_code: o.customer_code || "", commitment_date: o.target_dispatch || "",
  delivery_address: "", corresponding_address: "", items: [blankItem()], gst_percent: 18, termsText: "",
  required_date: "", po_validity: "", special_instructions: "",
});

const receiptTotals = (po) => {
  const receivedByItem = {};
  (po.grns || []).forEach((grn) => {
    (grn.entries || []).forEach((entry) => {
      receivedByItem[entry.item_id] =
        (receivedByItem[entry.item_id] || 0) + Number(entry.qty_received || 0);
    });
  });
  const lines = (po.items || []).filter((item) => Number(item.qty) > 0);
  const ordered = lines.reduce((total, item) => total + Number(item.qty || 0), 0);
  const received = lines.reduce(
    (total, item) => total + Math.min(receivedByItem[item.id] || 0, Number(item.qty || 0)),
    0,
  );
  return { ordered, received, balance: Math.max(0, ordered - received) };
};

function CheckRow({ label, done, onToggle, readOnly, note, busy, testid, auditInfo }) {
  return (
    <div className={`flex items-center justify-between gap-2 border rounded-sm px-3 py-2 ${done ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
      <div>
        <div className="text-xs font-semibold text-slate-800">{label}</div>
        {note && <div className="text-[10px] text-slate-500 mt-0.5">{note}</div>}
        {done && auditInfo?.by && (
          <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`${testid}-audit`}>
            Checked by {auditInfo.by}{auditInfo.at ? ` on ${(auditInfo.at || "").slice(0, 16).replace("T", " ")}` : ""}
          </div>
        )}
      </div>
      {readOnly || !onToggle ? (
        done ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" data-testid={testid} /> : <WarningCircle size={18} weight="fill" className="text-amber-600 shrink-0" data-testid={testid} />
      ) : (
        <Checkbox checked={!!done} disabled={busy} onCheckedChange={(v) => onToggle(!!v)} className="border-slate-400" data-testid={testid} />
      )}
    </div>
  );
}

/* Purchase Orders raised for one specific job — created inside Account Section by the Costing
   team, viewed/followed-up on the main Purchase Orders page. */
export function OrderPurchaseOrders({
  order,
  canManage,
  canCreatePurchaseOrders = false,
  canReceive = false,
  autoOpenPoNo,
  onAutoOpened,
  onFullScreenChange,
}) {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [poTemplate, setPoTemplate] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [po, setPo] = useState(blankPO(order));
  const [saving, setSaving] = useState(false);
  const [gateFor, setGateFor] = useState(null);
  const [gate, setGate] = useState(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [attUploading, setAttUploading] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [sendChannels, setSendChannels] = useState({ email: true, whatsapp: false });
  const [sending, setSending] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [followFor, setFollowFor] = useState(null);
  const [receiptFor, setReceiptFor] = useState(null);
  const [followEntry, setFollowEntry] = useState({ contact_person: "", method: "Phone Call", summary: "", next_commitment_date: "" });
  const [followSaving, setFollowSaving] = useState(false);
  const canRecordReceipt = canReceive || canManage || hasSpecialPermission(user, "store_receipts");
  useEffect(() => { onFullScreenChange?.(fullScreen); }, [fullScreen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onFullScreenChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    setLoading(true);
    api.get("/purchase-orders", { params: { order_id: order.id } }).then((r) => setPos(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/vendor-registry").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/settings/po-template").then((r) => r.data.exists && setPoTemplate(r.data)).catch(() => {});
    api.get("/company-settings").then((r) => setCompanySettings(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const openCreate = () => {
    setEditing(null);
    setPo({ ...blankPO(order), delivery_address: companySettings?.delivery_address || "" });
    setVendorWarning([]);
    setOpen(true);
    api.get("/settings/po-template/items").then((r) => {
      if (r.data.items?.length) setPo((p) => ({ ...p, items: r.data.items }));
    }).catch(() => {});
  };
  const openRevise = (p) => { setEditing(p); setPo({ ...blankPO(order), ...p, termsText: (p.terms || []).join("\n") }); setVendorWarning([]); setOpen(true); };
  /* Historical revision rows now open as a proper PDF snapshot (same vendor-format template),
     not the internal data-entry form — `revisions` is faked to length `i` so the template's
     "-R{i}" doc number / rev chip matches the row's own R{i} badge, with the actual edit that
     superseded this snapshot (rv) supplying the "REV · by · date" caption. */
  const printRevisionPdf = (p, rv, i) => {
    const revisions = Array.from({ length: i }, (_, idx) => (idx === i - 1 ? rv : {}));
    printPO({ ...p, ...rv.prev, revisions }, companySettings);
  };
  const openRepeat = (p) => {
    setEditing(null);
    setPo({
      ...blankPO(order), ...p, termsText: (p.terms || []).join("\n"),
      po_date: new Date().toISOString().slice(0, 10),
      items: (p.items || []).map((it) => ({ ...it, id: `it_${Date.now()}_${Math.floor(Math.random() * 999)}` })),
    });
    const v = vendors.find((x) => x.id === p.vendor_id);
    setVendorWarning(v ? vendorQualityIssues(v) : []);
    setOpen(true);
    toast.info("Repeating this PO — review the details and click Create to raise a new one");
  };
  const closeDialog = () => setOpen(false);

  const [vendorWarning, setVendorWarning] = useState([]);
  const onPickVendor = (vid) => {
    const v = vendors.find((x) => x.id === vid);
    if (!v) return;
    setPo((p) => ({
      ...p, vendor_id: vid, vendor_name: vendorTitle(v),
      vendor_address: pickField(v, ["address"]),
      concerned_person: pickField(v, ["contact person", "contact_person", "concerned"]),
      contact_phone: pickField(v, ["phone", "mobile"]),
      contact_email: pickField(v, ["email"]),
    }));
    const issues = vendorQualityIssues(v);
    setVendorWarning(issues);
    if (issues.length > 0) toast.warning(`${vendorTitle(v)} has ${issues.length} data-quality issue${issues.length > 1 ? "s" : ""} in the Vendor Registry — double-check before sending this PO`);
  };

  const setItem = (i, k, v) => setPo((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const addItem = () => setPo((p) => ({ ...p, items: [...p.items, blankItem()] }));
  const delItem = (i) => setPo((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const subtotal = po.items.reduce((s, it) => s + calcTotal(it), 0);
  const gstAmt = subtotal * ((Number(po.gst_percent) || 0) / 100);

  const importExcel = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    try {
      const { data: resp } = await api.post("/purchase-orders/import-excel", { data });
      setPo((p) => ({ ...p, items: resp.items }));
      toast.success(`Imported ${resp.imported} item(s)${resp.skipped ? `, skipped ${resp.skipped}` : ""}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not read this Excel file"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/settings/po-template/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = poTemplate?.name || "PO-Template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the template"); }
  };

  const save = async () => {
    if (!po.vendor_name.trim()) return toast.error("Pick a vendor or type the vendor name");
    setSaving(true);
    try {
      const payload = {
        ...po, order_id: order.id,
        terms: po.termsText.split("\n").map((t) => t.trim()).filter(Boolean),
        items: po.items.filter((it) => (it.item || "").trim()),
      };
      delete payload.termsText;
      let data;
      if (editing) ({ data } = await api.put(`/purchase-orders/${editing.id}`, payload));
      else ({ data } = await api.post("/purchase-orders", payload));
      toast.success(editing ? "Purchase Order updated" : "Purchase Order created — add attachments below if needed, then Close");
      setEditing(data);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save Purchase Order"); }
    finally { setSaving(false); }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !editing) return;
    const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    setAttUploading(true);
    try {
      const { data: updated } = await api.post(`/purchase-orders/${editing.id}/attachments`, { name: file.name, type: file.type, size: file.size, data });
      setEditing(updated);
      toast.success(`${file.name} attached`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setAttUploading(false); }
  };
  const viewAttachment = async (att) => {
    const w = window.open("about:blank", "_blank"); // open synchronously so browsers don't block the popup
    try {
      const { data } = await api.get(`/purchase-orders/${editing.id}/attachments/${att.id}/data`);
      const dataUrl = data.data;
      const parts = dataUrl.split(",");
      const mime = (parts[0].match(/data:(.*?);/) || [])[1] || "application/octet-stream";
      const bin = atob(parts[1]);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: mime }));
      if (w && !w.closed) w.location.href = url; else window.open(url, "_blank");
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(err.response?.data?.detail) || "Could not open this file");
    }
  };
  const deleteAttachment = async (att) => {
    if (!window.confirm(`Remove ${att.name}?`)) return;
    try { const { data } = await api.delete(`/purchase-orders/${editing.id}/attachments/${att.id}`); setEditing((e) => ({ ...e, attachments: (e.attachments || []).filter((a) => a.id !== att.id) })); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openFollow = (p) => {
    setFollowFor(p);
    setFollowEntry({ contact_person: p.concerned_person || "", method: "Phone Call", summary: "", next_commitment_date: "" });
  };
  const addFollow = async () => {
    if (!followEntry.summary.trim()) return toast.error("Enter what was discussed");
    setFollowSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${followFor.id}/followup`, followEntry);
      setFollowFor(data);
      setFollowEntry({ contact_person: data.concerned_person || "", method: "Phone Call", summary: "", next_commitment_date: "" });
      toast.success("Follow-up logged"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not log follow-up"); }
    finally { setFollowSaving(false); }
  };

  const openGate = async (p) => {
    setGateFor(p);
    setGate(null);
    try { const { data } = await api.get(`/purchase-orders/${p.id}/gate`); setGate(data); } catch { /* ignore */ }
  };
  const toggleCheck = async (check, done) => {
    setGateBusy(true);
    try { const { data } = await api.post(`/purchase-orders/${gateFor.id}/gate/${check}`, { done }); setGate(data); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update check"); }
    finally { setGateBusy(false); }
  };

  const openSend = (p) => {
    setSendFor(p);
    setSendChannels({ email: !!p.contact_email, whatsapp: !!p.contact_phone });
  };
  // "Place PO Now" shortcut from the red Workflow-tab alert badge — jumps straight to this PO's
  // Send-to-Vendor confirmation once it's loaded, instead of making the user hunt for it below.
  useEffect(() => {
    if (!autoOpenPoNo || loading) return;
    const p = pos.find((x) => x.po_no === autoOpenPoNo && x.status === "draft");
    if (p) openSend(p);
    onAutoOpened && onAutoOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenPoNo, loading, pos]);
  const confirmSend = async () => {
    const channels = Object.entries(sendChannels).filter(([, v]) => v).map(([k]) => k);
    if (!channels.length) return toast.error("Pick at least one channel");
    setSending(true);
    try {
      const { data } = await api.post(`/purchase-orders/${sendFor.id}/email`, { channels });
      if (data.channels_sent.length) toast.success(`PO sent via ${data.channels_sent.join(" & ")}`);
      else toast.warning(`PO marked sent, but delivery is pending — ${data.notes.join("; ")}`);
      setSendFor(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const downloadPdf = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${p.po_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const downloadExcel = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${p.po_no}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const delPo = async (p) => {
    if (!window.confirm(`Delete ${p.po_no}? This cannot be undone.`)) return;
    try { await api.delete(`/purchase-orders/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  // The stage-detail popup this panel lives in is a Radix Dialog whose translate/transform
  // establishes a CSS containing block for `position: fixed` descendants — so a plain
  // `fixed inset-0` here would only fill THAT popup, not the real browser viewport. Portalling
  // straight to document.body (same trick Radix Dialogs use internally) sidesteps that entirely.
  const content = (
    <>
    <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5"><Receipt size={14} className="text-emerald-700" /> Purchase Orders for this Job</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setFullScreen((s) => !s)} title={fullScreen ? "Exit full screen" : "Full screen"} data-testid="order-pos-fullscreen-toggle"
            className="h-7 px-2 text-xs flex items-center gap-1 border border-slate-300 rounded-sm text-slate-600 hover:bg-slate-50">
            {fullScreen ? <ArrowsIn size={13} /> : <ArrowsOut size={13} />} {fullScreen ? "Exit Full Screen" : "Full Screen"}
          </button>
          {canManage && canCreatePurchaseOrders && (
            <Button size="sm" onClick={openCreate} data-testid="order-po-create-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs gap-1">
              <Plus size={13} weight="bold" /> New Purchase Order
            </Button>
          )}
        </div>
      </div>
      <div className="border border-slate-300 bg-white rounded-sm overflow-x-auto shadow-sm" data-testid="order-pos-list">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-3 py-2">PO No</th><th className="px-3 py-2">Vendor</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">3-Check Gate</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2">Received / Balance</th><th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pos.map((p) => {
              const revs = p.revisions || [];
              return (
                <Fragment key={p.id}>
                  {revs.map((rv, i) => (
                    <tr key={`${p.id}-r${i}`} className="bg-slate-50/70" data-testid={`order-po-rev-row-${p.id}-${i}`}>
                      <td className="px-3 py-2 font-mono text-slate-500">
                        {p.po_no} <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1 py-0.5 rounded-sm ml-1" data-testid={`order-po-rev-tag-${p.id}-${i}`}>R{i}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">{rv.prev?.vendor_name}</td>
                      <td className="px-3 py-2 text-slate-500 capitalize">{rv.prev?.status} <span className="text-[9px] text-slate-400">· superseded</span></td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2 text-right font-mono text-slate-500">₹ {inr(rv.prev?.grand_total)}</td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => printRevisionPdf(p, rv, i)} title="View / Print this revision as PDF" data-testid={`order-po-rev-view-${p.id}-${i}`} className="p-1 text-slate-400 hover:text-emerald-700"><Eye size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  <tr data-testid={`order-po-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-emerald-700 font-semibold">
                      <button type="button" onClick={() => previewPO(p, companySettings)} className="hover:underline" title="Open full-screen preview" data-testid={`order-po-preview-${p.id}`}>{p.po_no}</button> {revs.length > 0 && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded-sm ml-1" data-testid={`order-po-rev-tag-${p.id}-current`}>R{revs.length}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{p.vendor_name}</td>
                    <td className="px-3 py-2 capitalize text-slate-600">{p.status}
                      {p.accepted_at ? (
                        <div className="text-[9px] font-mono text-emerald-600 flex items-center gap-1 mt-0.5" title={`Vendor confirmed on ${(p.accepted_at || "").slice(0, 16).replace("T", " ")}`} data-testid={`order-po-confirmed-${p.id}`}><CheckCircle size={10} weight="fill" /> Confirmed</div>
                      ) : p.opened_at ? (
                        <div className="text-[9px] font-mono text-amber-600 flex items-center gap-1 mt-0.5" title={`Vendor opened the PO link on ${(p.opened_at || "").slice(0, 16).replace("T", " ")}`} data-testid={`order-po-viewed-${p.id}`}><Eye size={10} weight="fill" /> Viewed</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => openGate(p)} data-testid={`order-po-checks-${p.id}`}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-400 text-amber-700 hover:bg-amber-50 transition-colors">
                        View Checks
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">₹ {inr(p.grand_total)}</td>
                    <td className="px-3 py-2" data-testid={`order-po-receipt-summary-${p.id}`}>
                      {(() => {
                        const totals = receiptTotals(p);
                        return totals.ordered > 0 ? (
                          <div className="space-y-1">
                            <div className="font-mono text-[11px] font-semibold text-emerald-700">
                              Received {totals.received} / {totals.ordered}
                            </div>
                            <div className="font-mono text-[10px] text-amber-700">
                              Balance {totals.balance}
                            </div>
                            {canRecordReceipt && p.status !== "cancelled" && p.status !== "draft" && (
                              <button
                                type="button"
                                onClick={() => setReceiptFor(p)}
                                className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 hover:underline"
                                data-testid={`order-po-record-receipt-${p.id}`}
                              >
                                Record receipt
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400">No qty lines</span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <button onClick={() => printPO(p, companySettings)} title="View / Print PO PDF — vendor format" data-testid={`order-po-view-${p.id}`} className="p-1 text-slate-400 hover:text-emerald-700"><Eye size={14} /></button>
                        {canManage && <button onClick={() => openRevise(p)} title="Revise" data-testid={`order-po-revise-${p.id}`} className="p-1 text-slate-400 hover:text-amber-600"><PencilSimple size={14} /></button>}
                        {canManage && <button onClick={() => delPo(p)} title="Delete" data-testid={`order-po-del-${p.id}`} className="p-1 text-slate-400 hover:text-red-500"><Trash size={14} /></button>}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button className="p-1 text-slate-400 hover:text-emerald-700" data-testid={`order-po-actions-${p.id}`}><DotsThreeVertical size={15} /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border-slate-200 text-slate-800">
                            {canManage && <DropdownMenuItem onClick={() => openSend(p)} data-testid={`order-po-send-${p.id}`}><PaperPlaneTilt size={13} className="mr-2" /> Send to Vendor</DropdownMenuItem>}
                            {canManage && canCreatePurchaseOrders && <DropdownMenuItem onClick={() => openRepeat(p)} data-testid={`order-po-repeat-${p.id}`}><CopySimple size={13} className="mr-2" /> Repeat Order</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => openFollow(p)} data-testid={`order-po-followup-${p.id}`}><ChatCircleDots size={13} className="mr-2" /> Follow-up Log {(p.followup_log || []).length > 0 ? `(${p.followup_log.length})` : ""}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadPdf(p)} data-testid={`order-po-pdf-${p.id}`}><DownloadSimple size={13} className="mr-2" /> Download PDF</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadExcel(p)} data-testid={`order-po-excel-${p.id}`}><FileXls size={13} className="mr-2" /> Download Excel</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
            {!loading && pos.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="order-pos-empty">
                No Purchase Orders raised for this job yet. {canManage && canCreatePurchaseOrders ? "Click \"New Purchase Order\" to raise one." : ""}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 mt-1">
        Every receipt updates the received and balance quantities shown here, in Purchase Orders, and in the Store workspace.
      </p>

      <MaterialReceiptDialog
        po={receiptFor}
        open={!!receiptFor}
        onOpenChange={(value) => !value && setReceiptFor(null)}
        onRecorded={() => {
          setReceiptFor(null);
          load();
        }}
        testIdPrefix="job-po-receipt"
      />

      {/* Create / Revise / View PO */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-none w-screen h-screen max-h-screen rounded-none flex flex-col bg-white text-slate-900 border-slate-200" data-testid="order-po-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editing ? `Revise ${editing.po_no}` : "New Purchase Order"} — Job {order.order_no}</DialogTitle>
            <DialogDescription className="text-slate-500">
              DVEPL Code, Order Date and Date of Commitment are carried over from this job — override if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className={lbl}>Vendor (from Vendor Registry)</label>
                <Select value={po.vendor_id || ""} onValueChange={onPickVendor}>
                  <SelectTrigger className={inputCls} data-testid="order-po-vendor-select"><SelectValue placeholder="Select vendor…" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{vendorTitle(v)}</SelectItem>)}</SelectContent>
                </Select>
                {vendorWarning.length > 0 && (
                  <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="order-po-vendor-quality-warning">
                    <WarningCircle size={13} className="mt-0.5 shrink-0" />
                    <span><b>Vendor Registry data-quality warning:</b> {vendorWarning.join(" · ")}. Fix this vendor's record or correct the fields below before sending.</span>
                  </div>
                )}
              </div>
              {!po.vendor_id && (
                <div><label className={lbl}>Vendor / Firm Name *</label>
                  <Input value={po.vendor_name} onChange={(e) => setPo({ ...po, vendor_name: e.target.value })} className={inputCls} placeholder="Or type a vendor not in the registry" data-testid="order-po-vendor-name" />
                </div>
              )}
              <div><label className={lbl}>Contact Person</label><Input value={po.concerned_person} onChange={(e) => setPo({ ...po, concerned_person: e.target.value })} className={inputCls} data-testid="order-po-contact-person" /></div>
              <div><label className={lbl}>Vendor Email</label><Input value={po.contact_email} onChange={(e) => setPo({ ...po, contact_email: e.target.value })} className={inputCls} data-testid="order-po-contact-email" /></div>
              <div><label className={lbl}>Vendor Phone</label><Input value={po.contact_phone} onChange={(e) => setPo({ ...po, contact_phone: e.target.value })} className={inputCls} data-testid="order-po-contact-phone" /></div>
              <div><label className={lbl}>DVEPL Code (job)</label><Input value={po.dvepl_code} onChange={(e) => setPo({ ...po, dvepl_code: e.target.value })} className={inputCls} data-testid="order-po-dvepl-code" /></div>
              <div><label className={lbl}>Project Ref</label><Input value={po.ref} onChange={(e) => setPo({ ...po, ref: e.target.value })} className={inputCls} data-testid="order-po-ref" /></div>
              <div><label className={lbl}>Date of Order</label><Input type="date" value={po.po_date} onChange={(e) => setPo({ ...po, po_date: e.target.value })} className={inputCls} data-testid="order-po-date" /></div>
              <div><label className={lbl}>Date of Commitment</label><Input type="date" value={po.commitment_date} onChange={(e) => setPo({ ...po, commitment_date: e.target.value })} className={inputCls} data-testid="order-po-commitment-date" /></div>
              <div className="md:col-span-2"><label className={lbl}>Vendor Address</label><Input value={po.vendor_address} onChange={(e) => setPo({ ...po, vendor_address: e.target.value })} className={inputCls} data-testid="order-po-vendor-address" /></div>
              <div className="md:col-span-2"><label className={lbl}>Delivery Address</label><textarea rows={2} value={po.delivery_address} onChange={(e) => setPo({ ...po, delivery_address: e.target.value })} className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500" data-testid="order-po-delivery-address" /></div>
              <div><label className={lbl}>Material Required By</label><Input type="date" value={po.required_date} onChange={(e) => setPo({ ...po, required_date: e.target.value })} className={inputCls} data-testid="order-po-required-date" /></div>
              <div><label className={lbl}>PO Valid Upto</label><Input type="date" value={po.po_validity} onChange={(e) => setPo({ ...po, po_validity: e.target.value })} className={inputCls} data-testid="order-po-validity" /></div>
              <div className="md:col-span-2"><label className={lbl}>Special Instructions (inspection/packing/urgent notes)</label><Input value={po.special_instructions} onChange={(e) => setPo({ ...po, special_instructions: e.target.value })} className={inputCls} data-testid="order-po-special-instructions" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                <label className={lbl}>Items — Import Template, Upload Excel, or add rows manually</label>
                <div className="flex items-center gap-2">
                  {poTemplate && (
                    <button type="button" onClick={downloadTemplate} className="text-[11px] text-emerald-700 hover:underline" data-testid="order-po-download-template">
                      Download blank template ({poTemplate.name})
                    </button>
                  )}
                  <label className="flex items-center gap-1 cursor-pointer bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 rounded-sm text-xs px-2 py-1">
                    <FileXls size={13} /> Upload Excel
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="order-po-import-excel" />
                  </label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} className="h-7 text-xs gap-1 border-slate-300" data-testid="order-po-add-item"><Plus size={13} /> Add Row</Button>
                </div>
              </div>
              <div className="border border-slate-300 rounded-sm overflow-x-auto">
                <table className="w-full text-xs border-collapse" role="grid">
                  <thead>
                    <tr>
                      <th className={`${xlTh} text-center w-8`}>#</th>
                      <th className={xlTh}>Item / Description</th>
                      <th className={xlTh}>Group</th>
                      <th className={xlTh}>Make</th>
                      <th className={xlTh}>Cat No</th>
                      <th className={`${xlTh} text-right w-20`}>Qty</th>
                      <th className={`${xlTh} text-right w-24`}>Rate</th>
                      <th className={`${xlTh} text-right w-16`}>Dis %</th>
                      <th className={`${xlTh} text-right w-28`}>Total</th>
                      <th className={`${xlTh} w-8`} />
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((it, i) => (
                      <tr key={it.id} data-testid={`order-po-item-row-${i}`}>
                        <td className={`${xlTd} text-center text-slate-400 bg-slate-50`}>{i + 1}</td>
                        <td className={xlTd}><input value={it.item} onChange={(e) => setItem(i, "item", e.target.value)} className={cellIn} data-testid={`order-po-item-name-${i}`} /></td>
                        <td className={xlTd}><input value={it.group} onChange={(e) => setItem(i, "group", e.target.value)} className={cellIn} data-testid={`order-po-item-group-${i}`} /></td>
                        <td className={xlTd}><input value={it.make} onChange={(e) => setItem(i, "make", e.target.value)} className={cellIn} data-testid={`order-po-item-make-${i}`} /></td>
                        <td className={xlTd}><input value={it.cat_no} onChange={(e) => setItem(i, "cat_no", e.target.value)} className={cellIn} data-testid={`order-po-item-catno-${i}`} /></td>
                        <td className={xlTd}><input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={`${cellIn} text-right`} data-testid={`order-po-item-qty-${i}`} /></td>
                        <td className={xlTd}><input type="number" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} className={`${cellIn} text-right`} data-testid={`order-po-item-rate-${i}`} /></td>
                        <td className={xlTd}><input type="number" value={it.dis} onChange={(e) => setItem(i, "dis", e.target.value)} className={`${cellIn} text-right`} data-testid={`order-po-item-dis-${i}`} /></td>
                        <td className={`${xlTd} bg-slate-50`}><div className="px-2 py-1.5 text-right font-mono text-slate-900">₹ {inr(calcTotal(it))}</div></td>
                        <td className={`${xlTd} text-center`}><button type="button" onClick={() => delItem(i)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`order-po-item-del-${i}`}><Trash size={13} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-6 mt-2 text-xs text-slate-600">
                <span>Subtotal: <b className="text-slate-900">₹ {inr(subtotal)}</b></span>
                <span className="flex items-center gap-1">GST% <Input type="number" value={po.gst_percent} onChange={(e) => setPo({ ...po, gst_percent: e.target.value })} className={`${inputCls} h-6 w-16 text-right`} data-testid="order-po-gst" />: <b className="text-slate-900">₹ {inr(gstAmt)}</b></span>
                <span>Grand Total: <b className="text-emerald-700">₹ {inr(subtotal + gstAmt)}</b></span>
              </div>
            </div>

            <div>
              <label className={lbl}>Terms &amp; Conditions (one per line)</label>
              <textarea rows={5} value={po.termsText} onChange={(e) => setPo({ ...po, termsText: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-xs font-mono focus:border-emerald-500 focus:outline-none" data-testid="order-po-terms" />
              <Button type="button" size="sm" variant="outline" onClick={() => setPo((p) => ({ ...p, termsText: `${p.termsText}${p.termsText ? "\n" : ""}` }))} className="mt-1.5 h-6 text-[11px] border-slate-300" data-testid="order-po-terms-add-row">+ Add Row</Button>
            </div>

            <div>
              <label className={lbl}>Attachments — Drawings, Quotes</label>
              {!editing ? (
                <p className="text-xs text-slate-400 border border-dashed border-slate-300 rounded-sm px-3 py-2">Save this Purchase Order first — attachments can be added right after.</p>
              ) : (
                <div className="border border-slate-300 rounded-sm p-2.5 space-y-1.5">
                  {(editing.attachments || []).map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5" data-testid={`order-po-att-${att.id}`}>
                      <button type="button" onClick={() => viewAttachment(att)} className="flex items-center gap-1.5 text-slate-700 hover:text-emerald-700 truncate" data-testid={`order-po-att-view-${att.id}`}>
                        <Paperclip size={13} className="shrink-0" /> <span className="truncate">{att.name}</span>
                      </button>
                      <button type="button" onClick={() => deleteAttachment(att)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`order-po-att-del-${att.id}`}><Trash size={13} /></button>
                    </div>
                  ))}
                  {(editing.attachments || []).length === 0 && <p className="text-xs text-slate-400">No attachments yet.</p>}
                  <label className="inline-flex items-center gap-1.5 cursor-pointer bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 rounded-sm text-xs px-2.5 py-1.5 mt-1">
                    <UploadSimple size={13} /> {attUploading ? "Uploading…" : "Upload Drawing / Quote"}
                    <input type="file" className="hidden" onChange={uploadAttachment} disabled={attUploading} data-testid="order-po-att-upload" />
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-slate-300" data-testid="order-po-cancel">{editing ? "Close" : "Cancel"}</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="order-po-save">
              {saving ? "Saving…" : editing ? "Save Revision" : "Create Purchase Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send / place PO — channel choice */}
      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="max-w-sm bg-white text-slate-900 border-slate-200" data-testid="order-po-send-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Send {sendFor?.po_no} to Vendor</DialogTitle>
            <DialogDescription className="text-slate-500">Choose how to notify {sendFor?.vendor_name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={sendChannels.email} disabled={!sendFor?.contact_email} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, email: !!v }))} data-testid="order-po-send-email" />
              Email {sendFor?.contact_email ? `(${sendFor.contact_email})` : "(no vendor email on file)"}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={sendChannels.whatsapp} disabled={!sendFor?.contact_phone} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, whatsapp: !!v }))} data-testid="order-po-send-whatsapp" />
              WhatsApp {sendFor?.contact_phone ? `(${sendFor.contact_phone})` : "(no vendor phone on file)"}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendFor(null)} className="border-slate-300" data-testid="order-po-send-cancel">Cancel</Button>
            <Button onClick={confirmSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="order-po-send-confirm">
              {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3-Check Gate */}
      <Dialog open={!!gateFor} onOpenChange={(v) => !v && setGateFor(null)}>
        <DialogContent className="max-w-md bg-white text-slate-900 border-slate-200" data-testid="order-po-gate-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">3-Check Gate — {gateFor?.po_no}</DialogTitle>
            <DialogDescription className="text-slate-500">All 3 checks must pass before this PO can be placed with the vendor. Admin can override if urgent.</DialogDescription>
          </DialogHeader>
          {gate ? (
            <div className="space-y-2">
              <CheckRow label="1. Customer Drawing Approval" done={gate.drawing_ok} readOnly note="Auto-derived from this job's workflow stage" testid="gate-drawing-check" auditInfo={gate.drawing_audit} />
              <CheckRow label="2. Costing Recheck (PO vs Drawing)" done={gate.costing_ok} onToggle={canManage ? (v) => toggleCheck("costing_recheck", v) : null} busy={gateBusy} testid="gate-costing-check" auditInfo={gate.costing_audit} />
              <CheckRow label="3. Store Stock Check" done={gate.store_ok} onToggle={canManage ? (v) => toggleCheck("store_check", v) : null} busy={gateBusy} testid="gate-store-check" auditInfo={gate.store_audit} />
              {gate.override && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-1.5" data-testid="gate-overridden-note">
                  Overridden by {gate.override.by} on {(gate.override.at || "").slice(0, 16).replace("T", " ")}
                </div>
              )}
              {gate.ready && !gate.override && (
                <div className="text-xs text-emerald-700 flex items-center gap-1.5 pt-1" data-testid="gate-ready-note"><CheckCircle size={14} weight="fill" /> Ready — this PO can be placed with the vendor.</div>
              )}
              {user.role === "admin" && !gate.ready && (
                <p className="text-[10px] text-slate-400 pt-1">Sending this PO to the vendor while checks are pending will be logged as an Admin override.</p>
              )}
            </div>
          ) : <div className="text-sm text-slate-500 py-4 text-center">Loading…</div>}
        </DialogContent>
      </Dialog>

      {/* Vendor Follow-up Log */}
      <Dialog open={!!followFor} onOpenChange={(v) => !v && setFollowFor(null)}>
        <DialogContent className="max-w-lg bg-white text-slate-900 border-slate-200" data-testid="order-po-followup-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Follow-up Log — {followFor?.po_no}</DialogTitle>
            <DialogDescription className="text-slate-500">Track every call/message to {followFor?.vendor_name} — who followed up, when, and what they committed to.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="border border-slate-300 rounded-sm p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><label className={lbl}>Contact Person</label><Input value={followEntry.contact_person} onChange={(e) => setFollowEntry((f) => ({ ...f, contact_person: e.target.value }))} className={inputCls} data-testid="order-po-followup-contact" /></div>
                <div><label className={lbl}>Method</label>
                  <Select value={followEntry.method} onValueChange={(v) => setFollowEntry((f) => ({ ...f, method: v }))}>
                    <SelectTrigger className={inputCls} data-testid="order-po-followup-method"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {["Phone Call", "WhatsApp", "Email", "Plant Visit", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><label className={lbl}>What was discussed *</label><textarea rows={2} value={followEntry.summary} onChange={(e) => setFollowEntry((f) => ({ ...f, summary: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-emerald-500 focus:outline-none" data-testid="order-po-followup-summary" /></div>
              <div><label className={lbl}>Next Commitment Date</label><Input type="date" value={followEntry.next_commitment_date} onChange={(e) => setFollowEntry((f) => ({ ...f, next_commitment_date: e.target.value }))} className={inputCls} data-testid="order-po-followup-nextdate" /></div>
              <Button size="sm" onClick={addFollow} disabled={followSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid="order-po-followup-add">{followSaving ? "Saving…" : "Log Follow-up"}</Button>
            </div>
            <div className="space-y-1.5" data-testid="order-po-followup-history">
              {(followFor?.followup_log || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No follow-ups logged yet.</p>}
              {[...(followFor?.followup_log || [])].reverse().map((f) => (
                <div key={f.id} className="text-xs border border-slate-200 rounded-sm px-2.5 py-1.5 bg-slate-50" data-testid={`order-po-followup-row-${f.id}`}>
                  <div className="flex items-center justify-between text-slate-500">
                    <span className="flex items-center gap-1"><ClockCounterClockwise size={11} /> {(f.date || "").slice(0, 16).replace("T", " ")} · {f.method} · {f.logged_by}</span>
                    {f.next_commitment_date && <span className="text-amber-600">Next: {f.next_commitment_date}</span>}
                  </div>
                  <div className="text-slate-800 mt-0.5">{f.summary}{f.contact_person ? ` — spoke with ${f.contact_person}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
  return fullScreen
    ? createPortal(
        <div
          className="fixed inset-0 z-[200] bg-white p-6 overflow-y-auto"
          data-testid="order-pos-section"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {content}
        </div>,
        document.body
      )
    : <div className="mb-5" data-testid="order-pos-section">{content}</div>;
}
